/**
 * SubMenuPanel — Shared slide-up panel container for all panel-type submenus.
 *
 * Handles only the slide animation — content is provided by the caller.
 * The panel slides in from below when `isOpen` is true, and slides back
 * out when false.
 *
 * Panel-type submenus (those that use this panel):
 *   'color' | 'rim' | 'scene' | 'animation'
 *
 * Overlay-type submenus ('info', 'ar', 'cookie', 'license') do NOT use
 * this panel — they render as fullscreen overlays / bottom sheets.
 *
 * Positioning: `bottom: 70px` places the panel above the MenuBar pill
 * (pill is at `bottom-5` = 20px, height 45px → total 65px + 5px gap = 70px).
 *
 * Animation: translateY(0) ↔ translateY(calc(100% + 200px))
 *   Using transform instead of bottom transition avoids layout thrashing.
 *   The extra 200px overshoot ensures it's fully off-screen on close.
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md#color--rim-submenu
 */

import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useUiTheme } from '../../hooks/useUiTheme';

interface SubMenuPanelProps {
  isOpen: boolean;
  submenuKey: string | null;
  children: ReactNode;
}

export function SubMenuPanel({ isOpen, submenuKey, children }: SubMenuPanelProps) {
  const theme = useUiTheme();
  const [visible, setVisible]     = useState(false);
  const [snapshot, setSnapshot]   = useState<ReactNode>(children);
  const childrenRef               = useRef<ReactNode>(children);
  const prevKeyRef                = useRef<string | null>(submenuKey);
  const timerRef                  = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  childrenRef.current = children;

  useEffect(() => {
    clearTimeout(timerRef.current);

    if (!isOpen) {
      setVisible(false);
      prevKeyRef.current = null;
      return;
    }

    const prevKey   = prevKeyRef.current;
    const switching = visible && prevKey !== null && submenuKey !== prevKey;
    prevKeyRef.current = submenuKey;

    if (switching) {
      // Slide down → swap content → slide up
      setVisible(false);
      timerRef.current = setTimeout(() => {
        setSnapshot(childrenRef.current);
        requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
      }, 280);
    } else {
      setSnapshot(childrenRef.current);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    }

    return () => clearTimeout(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, submenuKey]);

  return (
    <div
      className="absolute left-0 right-0 z-110"
      style={{
        bottom: '0px',
        transform: visible ? 'translateY(0)' : 'translateY(calc(100% + 120px))',
        transition: 'transform 350ms cubic-bezier(0.32, 0.72, 0, 1)',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      {/* Gradient backdrop — adapts to scene brightness */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: theme.panelGradient }}
      />
      {/* Content sits above the gradient */}
      <div className="relative">
        {snapshot}
      </div>
    </div>
  );
}
